import React, { useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw,
  Download,
  AlertCircle,
  AlertTriangle,
  Check,
  Target,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import {
  SalesKPICards,
  SalesPerformanceGrid,
  TargetAchievementChart,
  PaymentStatusSection,
  PipelineHealthSection,
  ExecutiveTable,
  PaymentsDrillDownModal,
} from '@/components/sales-dashboard';
import {
  DashboardPeriod,
  SalesDashboardData,
  PERIOD_OPTIONS,
} from '@/types/salesDashboard';

// Live Indicator Component
const LiveIndicator: React.FC = () => (
  <div className="flex items-center gap-2" aria-label="Live data indicator">
    <span className="relative flex h-2 w-2">
      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
      <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
    </span>
    <span className="text-xs text-green-600 font-medium">LIVE</span>
  </div>
);

export default function SalesDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [period, setPeriod] = useState<DashboardPeriod>('month');
  const [exportStatus, setExportStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [exportError, setExportError] = useState<string>('');
  const [drillDown, setDrillDown] = useState<null | 'received' | 'pending' | 'overdue'>(null);

  // Main dashboard query
  const {
    data: dashboardResponse,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ['sales-dashboard', period],
    queryFn: () => api.getSalesDashboardData({ period }),
    staleTime: 15000, // 15 seconds
    refetchInterval: 30000, // Auto-refresh every 30 seconds
  });

  const dashboardData: SalesDashboardData | undefined = dashboardResponse?.data;

  // Handle refresh
  const handleRefresh = () => {
    refetch();
  };

  // Handle period change
  const handlePeriodChange = (newPeriod: string) => {
    setPeriod(newPeriod as DashboardPeriod);
  };

  const handleExport = useCallback(async () => {
    setExportStatus('idle');
    setExportError('');
    try {
      const blob = await api.exportSalesDashboardData(period);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `sales-dashboard-${period}-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      setExportStatus('success');
      setTimeout(() => setExportStatus('idle'), 2500);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Export failed. Please try again.';
      setExportError(message);
      setExportStatus('error');
      setTimeout(() => setExportStatus('idle'), 4000);
    }
  }, [period]);

  // Handle payment card click — open inline drill-down modal showing the actual payments.
  // (Previously navigated to /finance which shows pending AR, not the received-payments breakdown
  // that the user actually wants to see.)
  const handlePaymentCardClick = (type: 'received' | 'pending' | 'overdue' | 'online' | 'cash') => {
    // 'online' / 'cash' method-specific tiles still go to finance (filtered) for now
    if (type === 'online' || type === 'cash') {
      const params = new URLSearchParams({ tab: 'all', method: type });
      navigate(`/finance?${params}`);
      return;
    }
    // received / pending / overdue — show inline modal
    setDrillDown(type);
  };

  // Handle executive click - navigate to their leads
  const handleExecutiveClick = (executiveId: string) => {
    navigate(`/pipeline?assignedTo=${executiveId}`);
  };

  // Format last updated time
  const getLastUpdatedText = () => {
    if (!dataUpdatedAt) return '';
    const date = new Date(dataUpdatedAt);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 overflow-x-auto">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Sales Dashboard
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
              Real-time sales performance and analytics
              {!!(isFetching && dashboardData) && (
                <span className="text-xs text-gray-400 animate-pulse">Updating...</span>
              )}
              {!!dataUpdatedAt && !isFetching && (
                <span className="text-xs text-gray-400">
                  • Updated at {getLastUpdatedText()}
                </span>
              )}
            </p>
          </div>

          <div className="flex items-center gap-3">
            <LiveIndicator />

            {/* Period Selector */}
            <Tabs value={period} onValueChange={handlePeriodChange}>
              <TabsList className="h-9">
                {PERIOD_OPTIONS.map((opt) => (
                  <TabsTrigger
                    key={opt.value}
                    value={opt.value}
                    className="text-sm px-4"
                  >
                    {opt.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isFetching}
              aria-label="Refresh dashboard data"
            >
              <RefreshCw
                className={`h-4 w-4 mr-2 ${isFetching ? 'animate-spin' : ''}`}
              />
              Refresh
            </Button>

            {(user?.role === 'CEO' || user?.role === 'GROUP_MANAGER') && (
              <div className="relative">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExport}
                  aria-label="Export dashboard data as CSV"
                  className={exportStatus === 'success' ? 'border-green-400 text-green-700' : exportStatus === 'error' ? 'border-red-400 text-red-700' : ''}
                >
                  {exportStatus === 'success' ? (
                    <Check className="h-4 w-4 mr-2" />
                  ) : (
                    <Download className="h-4 w-4 mr-2" />
                  )}
                  {exportStatus === 'success' ? 'Exported!' : exportStatus === 'error' ? 'Failed' : 'Export'}
                </Button>
                {exportStatus === 'error' && exportError && (
                  <div className="absolute top-full right-0 mt-1 w-56 bg-red-50 border border-red-200 rounded-md p-2 text-xs text-red-700 shadow-md z-10">
                    {exportError}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Error State */}
        {isError && !dashboardData && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" />
            <h3 className="text-lg font-semibold text-red-800 mb-1">
              Failed to load dashboard
            </h3>
            <p className="text-sm text-red-600 mb-4">
              {error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.'}
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              aria-label="Retry loading dashboard data"
              className="border-red-300 text-red-700 hover:bg-red-100"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Retry
            </Button>
          </div>
        )}

        {/* Top KPI Cards */}
        <SalesKPICards
          kpis={(dashboardData?.kpis && typeof dashboardData.kpis === 'object') ? dashboardData.kpis : { monthlyTarget: 0, monthlyAchieved: 0, totalSales: 0, netRevenue: 0, activePipeline: 0 }}
          isLoading={isLoading && !dashboardData}
          onCardClick={(key) => {
            // Collections (MTD) tile → open the inline drill-down modal (same as Payment Status card)
            if (key === 'netRevenue') {
              setDrillDown('received');
              return;
            }
            // IST-safe YYYY-MM-DD (avoids .toISOString().slice(0,10) UTC drift —
            // April 1 local → March 31 UTC issue)
            const now = new Date();
            const y = now.getFullYear();
            const m = String(now.getMonth() + 1).padStart(2, '0');
            const lastDay = new Date(y, now.getMonth() + 1, 0).getDate();
            const monthStart = `${y}-${m}-01`;
            const monthEnd = `${y}-${m}-${String(lastDay).padStart(2, '0')}`;
            const routes: Record<string, string> = {
              monthlyTarget: '/revenue-targets',
              totalSales: `/pipeline?stage=WON&period=${period}`,
              netRevenue: `/finance?dateFrom=${monthStart}&dateTo=${monthEnd}`,
              activePipeline: '/pipeline',
            };
            navigate(routes[key] || '/pipeline');
          }}
        />

        {/* Missing targets banner */}
        {dashboardData && dashboardData.kpis.monthlyTarget === 0 && dashboardData.kpis.totalSales === 0 && (
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <p className="text-sm text-amber-800 flex-1">
              No revenue targets configured for{' '}
              <span className="font-medium">
                {new Date().toLocaleString('en-IN', { month: 'long', year: 'numeric' })}
              </span>
              . Set targets to track your team's progress.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="border-amber-300 text-amber-700 hover:bg-amber-100 flex-shrink-0"
              onClick={() => navigate('/revenue-targets')}
            >
              <Target className="w-4 h-4 mr-1" />
              Set Targets
            </Button>
          </div>
        )}

        {/* Sales Performance Grid */}
        <SalesPerformanceGrid
          performance={dashboardData?.performance ?? { salesPeriod: 0, periodTarget: 0, targetAchieved: 0, remainingTarget: 0, dealsWon: 0, dealsWonMonth: 0, pipelineValue: 0 }}
          period={period}
          isLoading={isLoading && !dashboardData}
          onMetricClick={(key) => {
            const routes: Record<string, string> = {
              salesPeriod: '/pipeline?stage=WON',
              periodTarget: '/target-dashboard',
              targetAchieved: '/target-dashboard',
              remainingTarget: '/target-dashboard',
              dealsWon: '/pipeline?stage=WON',
              pipelineValue: '/pipeline',
            };
            navigate(routes[key] || '/pipeline');
          }}
        />

        {/* Two Column Layout: Target Achievement + Payment Status */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Target Achievement Chart */}
          <TargetAchievementChart
            targetProgress={dashboardData?.targetProgress ?? { percentage: 0, achieved: 0, remaining: 0, daysLeft: 0, target: 0 }}
            isLoading={isLoading && !dashboardData}
            bookings={dashboardData?.kpis.monthlyAchieved ?? 0}
            collections={dashboardData?.kpis.netRevenue ?? 0}
          />

          {/* Payment Status Section */}
          <PaymentStatusSection
            payments={dashboardData?.payments ?? { received: { count: 0, amount: 0 }, pending: { count: 0, amount: 0 }, overdue: { count: 0, amount: 0 }, byMethod: { online: { count: 0, amount: 0 }, cash: { count: 0, amount: 0 } }, totalReceived: 0, totalReceivedCount: 0 }}
            period={period}
            isLoading={isLoading && !dashboardData}
            onCardClick={handlePaymentCardClick}
          />
        </div>

        {/* Pipeline Health Section */}
        <PipelineHealthSection
          pipeline={dashboardData?.pipeline ?? { byStage: [], expectedWeek: { count: 0, value: 0 }, expectedMonth: { count: 0, value: 0 }, byPriority: { HOT: 0, WARM: 0, COLD: 0 } }}
          isLoading={isLoading && !dashboardData}
          onStageClick={(stage) => navigate(`/pipeline?stage=${stage}`)}
          onPriorityClick={(priority) => navigate(`/pipeline?priority=${priority}`)}
        />

        {/* Executive Performance Table */}
        <ExecutiveTable
          executives={dashboardData?.executives ?? []}
          period={period}
          isLoading={isLoading && !dashboardData}
          onExecutiveClick={handleExecutiveClick}
        />
      </div>

      {/* Payments drill-down modal — opens on Collections (MTD) or Payment Status card click */}
      <PaymentsDrillDownModal
        open={drillDown !== null}
        type={drillDown ?? 'received'}
        period={period}
        onClose={() => setDrillDown(null)}
      />
    </DashboardLayout>
  );
}
